import { Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { IteroBaseEvent } from '@bootstrap/models/base-event.model';
import { EventBusService } from '@bootstrap/services/event-bus.service';
import { EventName } from '@itero/itero-client-communication';
import { filter, share } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class EventPipelineService {
	// Creates a hot observable that subscribes to `eventBusService` only once due to `share()`.
	// The subscription is shared among all subscribers and automatically unsubscribes
	// when all subscribers complete or when the source observable completes.
	private readonly events$: Observable<IteroBaseEvent> = new Observable<IteroBaseEvent>(subscriber => {
		const handleError = (e: Error) => {
			const message = e?.message ?? 'Rx-App: Something went wrong while connecting to Event Bus';

			this.logger.error(message, { module: EventPipelineService.name, error: e });

			if (e) {
				throw e;
			}
		};

		let result: { success: boolean; error?: Error };

		try {
			result = this.eventBusService.subscribe(event => {
				subscriber.next(event);
			});

			if (!result.success) {
				handleError(result.error as Error);
			}
		} catch (error) {
			handleError(error as Error);
		}

		return () => {
			this.eventBusService.unsubscribe();
			subscriber.unsubscribe();
		};
	}).pipe(
		share({
			connector: () => new Subject<IteroBaseEvent>(),
			resetOnRefCountZero: true,
			resetOnComplete: true,
			resetOnError: true
		})
	);

	constructor(
		private eventBusService: EventBusService,
		private logger: LoggerService
	) {}

	fromEvent<T>(eventName: EventName): Observable<T> {
		return this.events$.pipe(filter(event => event.eventHeader.eventName === eventName)) as Observable<T>;
	}
}
